import React from 'react';
import { useTranslation } from 'react-i18next';
import { SelectChangeEvent } from '@mui/material/Select';
import {
  Grid,
  TextField,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { businessTypeKeys, industryAndServiceKeys, CustomField } from '../../types/user';

interface Props {
  companyName: string;
  businessType: CustomField;
  siret: string;
  industry: CustomField;
  specialization: string;
  description: string;
  onInputChange: (field: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onSelectChange: (field: string) => (event: SelectChangeEvent, isCustomField?: boolean) => void;
}

const CompanyFields: React.FC<Props> = ({
  companyName,
  businessType,
  siret,
  industry,
  specialization,
  description,
  onInputChange,
  onSelectChange,
}) => {
  const { t, i18n } = useTranslation();
  const isFrench = i18n.language === 'fr';
  const currentBusinessTypes = businessTypeKeys[isFrench ? 'fr' : 'en'];

  return (
    <Paper elevation={0} className="glass-effect" sx={{ p: 3, borderRadius: 2 }}>
      <Typography 
        variant="h6" 
        className="animated-gradient-text" 
        gutterBottom 
        sx={{ 
          mb: 4,
          fontFamily: 'Poppins, sans-serif'
        }}
      >
        {t('registration.generalInfo')}
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t('registration.companyName')}
            value={companyName}
            onChange={onInputChange('companyName')}
            required
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth required>
            <InputLabel>{t('registration.businessType')}</InputLabel>
            <Select
              value={businessType.value}
              onChange={(e) => onSelectChange('businessType')(e, true)}
              label={t('registration.businessType')}
            >
              {currentBusinessTypes.map(key => (
                <MenuItem key={key} value={key}>
                  {t(`registration.businessTypes.${key}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t('registration.registration_number')}
            value={siret}
            onChange={onInputChange('siret')}
            required={isFrench}
            helperText={isFrench ? undefined : "Optional"}
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth required>
            <InputLabel>{t('registration.industry')}</InputLabel>
            <Select
              value={industry.value}
              onChange={(e) => onSelectChange('industry')(e, true)}
              label={t('registration.industry')}
            >
              {industryAndServiceKeys.map(key => (
                <MenuItem key={key} value={key}>
                  {t(`registration.industries.${key}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {businessType.isOther && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label={t('registration.businessTypes.other')}
              value={businessType.customValue || ''}
              onChange={onInputChange('businessType.customValue')}
              required
              variant="outlined"
            />
          </Grid>
        )}
        {industry.isOther && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label={t('registration.industries.other_placeholder')}
              value={industry.customValue || ''}
              onChange={onInputChange('industry.customValue')}
              required
              variant="outlined"
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t('registration.specialization')}
            value={specialization}
            onChange={onInputChange('specialization')}
            variant="outlined"
            inputProps={{ maxLength: 50 }}
            helperText={`${specialization.length}/50`}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={4}
            label={t('registration.description')}
            value={description}
            onChange={onInputChange('description')}
            required
            variant="outlined"
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default CompanyFields;
